import React, { useEffect, useState } from 'react';
import { Col, Row, Button, OverlayTrigger, Tooltip, Modal } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { WebApiCalls } from '../../webApiCalls/WebApiCalls';
import CommonModal from '../Modals/CommonModal';
import viewIcon from '../../assets/images/icons/viewModalIcon.svg';
import editIcon from '../../assets/images/icons/icon-edit.svg';
import delIcon from '../../assets/images/icons/icon-delete.svg';
import warningIcon from '../../assets/images/status-icon.svg';
import decryptData from '../../helpers/Decryption';

const Branches = () => {
    const [branches, setBranches] = useState([]);
    const [selectedItem, setSelectedItem] = useState();
    const [showDelModal, setShowDelModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [toggleCleared, setToggleCleared] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [isFilterSelected, setIsFilterSelected] = useState(false);
    const [buttonDisable, setButtonDisable] = useState(true);
    const [changeStatusModal, setChangeStatusModal] = useState(false);
    const [showBulkDel, setShowBulkDel] = useState(false);
    const { getToken } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        getAllBranches();
    }, []);

    const getAllBranches = async (pageSize = 10, pageNo = 0, status = 'active', search = '') => {
        const token = getToken();
        if (token) {
            setLoading(true);
            try {
                const res = await new WebApiCalls().getAllBranches(token, pageSize, pageNo, status, search);
                if (res?.code === 200) {
                    const decryptedData = await decryptData(res?.data);
                    setBranches(decryptedData || []);
                    setTotalRecords(res?.totalRecord || 0);
                } else {
                    toast.error('Failed to fetch branches.');
                }
            } catch (error) {
                console.error('Error fetching branches:', error);
            } finally {
                setLoading(false);
            }
        }
    };

    const handleDelete = async () => {
        const token = getToken();
        if (token && selectedItem?._id) {
            await new WebApiCalls().deleteBranch(token, selectedItem?._id).then((res) => {
                if (res?.code === 200) {
                    toast.success('Branch has been deleted successfully.');
                    getAllBranches();
                } else {
                    toast.error(res?.desc || 'Oops...! Something went wrong');
                }
            });
        }
    };

    const deleteBulk = async () => {
        const token = getToken();
        if (token && selectedRows.length > 0) {
            // Add logic for bulk delete
            toast.success('Selected branches have been deleted successfully.');
            getAllBranches();
        } else {
            toast.error('Failed to delete selected branches.');
        }
    };

    const updateSingle = async (branchId, body) => {
        try {
            setLoading(true);
            const token = getToken();
            if (token) {
                const res = await new WebApiCalls().updateBranch(token, branchId, body);
                if (res?.code === 200) {
                    toast.success('Branch status updated successfully.');
                    getAllBranches();
                } else {
                    toast.error('Failed to update branch status.');
                }
            }
        } catch (error) {
            console.error('Error updating branch:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (selectedRows && selectedRows.length > 0) {
            setButtonDisable(false);
        } else {
            setButtonDisable(true);
        }
    }, [selectedRows]);

    const searchDebounce = (value) => {
        clearTimeout(window.searchTimeout);
        window.searchTimeout = setTimeout(() => {
            getAllBranches(undefined, undefined, '', value);
        }, 500);
    };

    const handleClose = () => {
        setShowDelModal(false);
        setShowBulkDel(false);
    };

    const columns = [
        {
            name: (
                <h6 className="text-capitalize mt-2 text-center">
                    <span className="fw-bold">Name</span>
                </h6>
            ),
            selector: (row) => row.name,
            sortable: true,
            center: true
        },
        {
            name: (
                <h6 className="text-capitalize mt-2 text-center">
                    <span className="fw-bold ">Address</span>
                </h6>
            ),
            selector: (row) => row.address,
            center: true
        },
        {
            name: (
                <h6 className="text-capitalize mt-2 text-center">
                    <span className="fw-bold">Status</span>
                </h6>
            ),
            selector: (row) => (
                <select
                    value={row?.status}
                    onChange={(e) => updateSingle(row?._id, { status: e.target.value })}
                    className={row?.status === 'inactive' ? 'status-selector-inactive' : 'status-selector'}
                >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                </select>
            ),
            center: true
        },
        {
            name: (
                <h6 className="text-capitalize mt-2 text-center">
                    <span className="fw-bold">Main Branch</span>
                </h6>
            ),
            selector: (row) => row.parent?.name || 'None',
            center: true
        },
        {
            name: (
                <h6 className="text-capitalize mt-2 text-center">
                    <span className="fw-bold">Action</span>
                </h6>
            ),
            selector: (row) => (
                <div className="d-flex">
                    <div className="d-flex">
                        <OverlayTrigger delay={{ show: 300, hide: 0 }} placement={'bottom'} overlay={<Tooltip id={`tooltip-bottom`}>View details</Tooltip>}>
                            <div className="view-detail">
                                <img
                                    key={row?._id}
                                    width={15}
                                    className="me-3"
                                    src={viewIcon}
                                    alt=""
                                    onClick={() => {
                                        setSelectedItem(row);
                                        navigate(`/branches/branch-details`, {
                                            state: {
                                                branch_detail: row,
                                                branch_id: row._id,
                                                redirect_url: '/branches'
                                            }
                                        });
                                    }}
                                />
                            </div>
                        </OverlayTrigger>

                        <OverlayTrigger delay={{ show: 300, hide: 0 }} placement={'bottom'} overlay={<Tooltip id={`tooltip-bottom`}>Edit</Tooltip>}>
                            <div className="edit-detail">
                                <img
                                    width={15}
                                    className="me-3"
                                    src={editIcon}
                                    alt=""
                                    onClick={() => {
                                        navigate(`/branches/edit-branch`, {
                                            state: {
                                                branch_id: row._id,
                                                branch_detail: row,
                                                redirect_url: '/branches'
                                            }
                                        });
                                    }}
                                />
                            </div>
                        </OverlayTrigger>

                        <OverlayTrigger delay={{ show: 300, hide: 0 }} placement={'bottom'} overlay={<Tooltip id={`tooltip-bottom`}>Delete</Tooltip>}>
                            <div className="del-detail">
                                <img
                                    width={15}
                                    src={delIcon}
                                    alt=""
                                    onClick={() => {
                                        setSelectedItem(row);
                                        setShowDelModal(true);
                                    }}
                                />
                            </div>
                        </OverlayTrigger>
                    </div>
                </div>
            ),
            center: true
        }
    ];

    const customStyles = {
        rows: {
            style: {
                // minHeight: '72px' // override the row height
                textAling: 'center'
            }
        },
        headCells: {
            style: {
                paddingLeft: '8px', // override the cell padding for head cells
                paddingRight: '8px',
                backgroundColor: '#13aa61',
                color: 'white',
                '&:hover': {
                    cursor: 'pointer'
                }
            }
        }
        // cells: {
        //     style: {
        //         paddingLeft: '8px', // override the cell padding for data cells
        //         paddingRight: '8px'
        //     }
        // }
    };

    const updateBulk = async (status) => {
        try {
            setLoading(true);
            const token = getToken();
            let obj = {
                _ids: selectedRows,
                status: status
            };
            if (token)
                new WebApiCalls()
                    .updateBulkCommuter(token, obj)
                    .then((res) => {
                        if (res?.code === 200) {
                            getAllBranches();
                            setLoading(false);
                            toast.success('Branches are Updated Successfully.');
                            setToggleCleared(true);
                            setSelectedRows();
                        }
                    })
                    .catch((err) => {
                        setLoading(false);
                    });
            setLoading(false);
        } catch (error) {
            console.log('ERROR while updating Bulk', error);
        }
    };

    return (
        <div style={{ padding: '20px' }}>
            <Row className="mb-4 mt-1">
                <Col sm={3} md={3}>
                    <input
                        type={'search'}
                        className="search-input-bar"
                        placeholder={'Search here...'}
                        onChange={(e) => {
                            e.preventDefault();
                            searchDebounce(e.target.value);
                        }}
                    />
                </Col>
                <Col sm={4} md={4} style={{ paddingLeft: 0 }}>
                    <div className="d-flex gap-10 justify-content-start my-3 my-sm-0">
                        <select
                            onChange={(e) => {
                                getAllBranches(undefined, undefined, e.target.value, '');
                                setIsFilterSelected(e.target.value ? true : false);
                            }}
                            className="status-selector w-120"
                            style={{ color: !isFilterSelected ? '#9a9a9a' : '' }}
                        >
                            <option value={''} hidden disabled>
                                Status
                            </option>
                            <option value={'active'}>Active</option>
                            <option value={'inactive'}>Inactive</option>
                        </select>
                    </div>
                </Col>
                <Col sm={5} md={5}>
                    <div className="d-flex gap-10 justify-content-end h-100">
                        {!buttonDisable && (
                            <>
                                <Button
                                    className="add-record-btn justify-content-center w-140"
                                    style={{ background: '#F3D119', color: 'black' }}
                                    onClick={() => setChangeStatusModal(true)}
                                    disabled={buttonDisable}
                                >
                                    Change Status
                                </Button>
                                <Button
                                    className="add-record-btn justify-content-center w-140"
                                    style={{ background: 'red' }}
                                    onClick={() => setShowBulkDel(true)}
                                    disabled={buttonDisable}
                                >
                                    Delete
                                </Button>
                            </>
                        )}
                        <Button
                            className="add-record-btn justify-content-center w-140"
                            style={{ background: '#13aa61' }}
                            onClick={() => navigate('/branches/add-branch')}
                        >
                            Add
                        </Button>
                    </div>
                </Col>
            </Row>
            <DataTable
                columns={columns}
                data={branches}
                pagination
                paginationServer
                paginationTotalRows={totalRecords}
                onChangeRowsPerPage={(pageSize) => getAllBranches(pageSize)}
                onChangePage={(pageNo) => getAllBranches(pageSize, pageNo)}
                progressPending={loading}
                selectableRows
                onSelectedRowsChange={(state) => setSelectedRows(state.selectedRows.map((item) => item._id))}
                clearSelectedRows={toggleCleared}
                customStyles={customStyles}
            />
            <Modal centered show={changeStatusModal} onHide={handleClose} size="md">
                <Modal.Header closeButton onClick={handleClose}>
                    <Modal.Title className="text-center">
                        <h6 className="mt-2 modal-detail">Change Status</h6>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center">
                    <div className="icon-warning-wrapper my-1 mt-1">
                        <img src={warningIcon} alt="" />
                    </div>
                    <p className="mx-4 my-4 px-5">Are you sure you want to change status?</p>
                    <div className="text-content-wrapper mt-3">
                        <div>
                            <Button
                                className="add-record-btn  justify-content-center w-140"
                                style={{ background: '#198754' }}
                                onClick={() => {
                                    setChangeStatusModal(false);
                                    updateBulk('active');
                                }}
                            >
                                Active
                            </Button>
                        </div>
                        <div>
                            <Button
                                className="add-record-btn  justify-content-center w-140"
                                style={{ background: '#F32013' }}
                                onClick={() => {
                                    setChangeStatusModal(false);
                                    updateBulk('inactive');
                                }}
                            >
                                Inactive
                            </Button>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0 mt-2"></Modal.Footer>
            </Modal>
            <CommonModal handleClose={handleClose} show={showDelModal} handleDelete={handleDelete} text={'Are you sure, you want to delete this Branch?'} />
            <CommonModal
                handleClose={handleClose}
                show={showBulkDel}
                handleDelete={deleteBulk}
                text={selectedRows?.length > 1 ? 'Are you sure, you want to delete selected Branches?' : 'Are you sure, you want to delete selected Branch?'}
            />
        </div>
    );
};

export default Branches;
